import React, { useState } from 'react';
import { Download, Search, Loader, AlertCircle } from 'lucide-react';
import { formatDate, formatDateForInput, todayStr, monthsAgoStr } from '../utils/dateUtils';
import MaskedDatePicker from './MaskedDatePicker';
import * as XLSX from 'xlsx';

const API_URL = 'https://apisaleeasy.psisat.com/api/sales/customer-product-breakdown';

const getDefaultDates = () => ({
  DateFrom: monthsAgoStr(6),
  DateTo: todayStr(),
});

const PRODUCT_TYPES = ['SAT', 'Solar', 'MTF', 'MTB', 'Other'];

const TYPE_COLORS = {
  SAT:   'bg-blue-100 text-blue-800',
  Solar: 'bg-yellow-100 text-yellow-800',
  MTF:   'bg-green-100 text-green-800',
  MTB:   'bg-teal-100 text-teal-800',
  Other: 'bg-gray-100 text-gray-700',
};

const CustomerProductBreakdownComponent = () => {
  const defaults = getDefaultDates();
  const [filters, setFilters] = useState({
    DateFrom: defaults.DateFrom,
    DateTo: defaults.DateTo,
    CompanyCode: '045',
    sort_by: 'amount',
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(filters),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      if (result.success && result.data) {
        setData(result.data);
      } else {
        setError(result.message || 'ไม่พบข้อมูล');
      }
    } catch (err) {
      setError(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();

    const header = [
      'อันดับ', 'รหัสลูกค้า', 'ชื่อลูกค้า', 'รหัสพนักงานขาย',
      'จำนวน Invoice', 'วันที่ซื้อล่าสุด', 'วันที่ซื้อแรก',
      'SAT', 'Solar', 'MTF (โรงงาน)', 'MTB (นำเข้า)', 'Other',
    ];
    const rows = (data.customers || []).map((c, i) => [
      i + 1,
      c.cust_no,
      c.cust_name,
      c.salesman_code,
      c.invoice_count,
      c.last_invoice_date ? formatDate(c.last_invoice_date) : '-',
      c.first_invoice_date ? formatDate(c.first_invoice_date) : '-',
      (c.product_type_breakdown?.SAT?.amount || 0) > 0 ? '✓' : '',
      (c.product_type_breakdown?.Solar?.amount || 0) > 0 ? '✓' : '',
      (c.product_type_breakdown?.MTF?.amount || 0) > 0 ? '✓' : '',
      (c.product_type_breakdown?.MTB?.amount || 0) > 0 ? '✓' : '',
      (c.product_type_breakdown?.Other?.amount || 0) > 0 ? '✓' : '',
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    XLSX.utils.book_append_sheet(wb, ws, 'ลูกค้า-ประเภทสินค้า');

    XLSX.writeFile(
      wb,
      `ลูกค้า-ประเภทสินค้า_${filters.DateFrom}_${filters.DateTo}_${filters.CompanyCode}.xlsx`
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">สัดส่วนสินค้าของลูกค้า</h2>
          <p className="text-sm text-gray-500 mt-1">
            วิเคราะห์ว่าลูกค้าแต่ละรายซื้อสินค้าประเภทไหน (SAT / Solar / MTF / MTB / Other)
          </p>
        </div>
        {data && (
          <button
            onClick={exportToExcel}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Download className="w-4 h-4" />
            Export Excel
          </button>
        )}
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">วันเริ่มต้น</label>
            <MaskedDatePicker
              value={filters.DateFrom}
              onChange={(str) => setFilters(f => ({ ...f, DateFrom: str }))}
              placeholderText="DD/MM/YYYY"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">วันสิ้นสุด</label>
            <MaskedDatePicker
              value={filters.DateTo}
              onChange={(str) => setFilters(f => ({ ...f, DateTo: str }))}
              placeholderText="DD/MM/YYYY"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">บริษัท</label>
            <select
              value={filters.CompanyCode}
              onChange={e => setFilters(f => ({ ...f, CompanyCode: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="045">045 - PSI</option>
              <option value="043">043 - MAQ</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">เรียงตาม</label>
            <div className="flex gap-2">
              <select
                value={filters.sort_by}
                onChange={e => setFilters(f => ({ ...f, sort_by: e.target.value }))}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="amount">ยอดรวม</option>
                <option value="date">วันที่ล่าสุด</option>
              </select>
              <button
                onClick={fetchData}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center gap-1 transition-colors"
              >
                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                ค้นหา
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 mb-4">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20 text-gray-500">
          <Loader className="w-6 h-6 animate-spin mr-2" />
          <span>กำลังโหลดข้อมูล...</span>
        </div>
      )}

      {/* Summary */}
      {!loading && data && (
        <>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex flex-wrap gap-6 text-sm">
            <div><span className="text-gray-500">ลูกค้าทั้งหมด:</span> <strong>{data.summary.total_customers.toLocaleString()} ราย</strong></div>
            <div><span className="text-gray-500">Invoice ทั้งหมด:</span> <strong>{data.summary.total_invoices.toLocaleString()} ใบ</strong></div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600 w-12">#</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">รหัสลูกค้า</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">ชื่อลูกค้า</th>
                    <th className="px-4 py-3 text-center font-medium text-gray-600">Invoice</th>
                    <th className="px-4 py-3 text-center font-medium text-blue-600">SAT</th>
                    <th className="px-4 py-3 text-center font-medium text-yellow-600">Solar</th>
                    <th className="px-4 py-3 text-center font-medium text-green-600">MTF</th>
                    <th className="px-4 py-3 text-center font-medium text-teal-600">MTB</th>
                    <th className="px-4 py-3 text-center font-medium text-gray-500">Other</th>
                    <th className="px-4 py-3 text-center font-medium text-gray-600">ซื้อล่าสุด</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(data.customers || []).map((c, i) => (
                    <tr key={c.cust_no} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-400 text-xs">{i + 1}</td>
                      <td className="px-4 py-3 font-mono text-gray-700 text-xs">{c.cust_no}</td>
                      <td className="px-4 py-3 text-gray-900 font-medium">{c.cust_name || '-'}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{c.invoice_count}</td>
                      {PRODUCT_TYPES.map(t => (
                        <td key={t} className="px-4 py-3 text-center">
                          {(c.product_type_breakdown?.[t]?.amount || 0) > 0 ? (
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${TYPE_COLORS[t]}`}>✓</span>
                          ) : (
                            <span className="text-gray-200">—</span>
                          )}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-center text-xs text-gray-500">
                        {c.last_invoice_date
                          ? formatDate(c.last_invoice_date)
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.customers?.length === 0 && (
                <div className="py-16 text-center text-gray-400">ไม่พบข้อมูลลูกค้าในช่วงเวลาที่เลือก</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Empty state */}
      {!loading && !data && !error && (
        <div className="py-20 text-center text-gray-400">
          <Search className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>กดปุ่ม "ค้นหา" เพื่อดูข้อมูลสัดส่วนสินค้าของลูกค้า</p>
        </div>
      )}
    </div>
  );
};

export default CustomerProductBreakdownComponent;
